import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format, addDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { tr } from "date-fns/locale";
import { Check, X, Copy, RefreshCw, Star, Frown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const API = window.location.origin;
const GROUP_CAT = { "dinner-cruise": 1, "sunset-cruise": 2, "small-group-sunset": 3, "daytime": 4 };
const GROUP_TONE = {
  "dinner-cruise": "text-amber-300 border-amber-400/40",
  "sunset-cruise": "text-orange-300 border-orange-400/40",
  "small-group-sunset": "text-sky-300 border-sky-400/40",
  "daytime": "text-emerald-300 border-emerald-400/40",
  "other": "text-slate-300 border-slate-500/40",
};
const fmtN = (n) => (n || 0).toLocaleString("tr-TR");
const iso = (d) => format(d, "yyyy-MM-dd");

const PRESETS = [
  { label: "Bu Ay", range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Geçen Ay", range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
  { label: "Son 30 Gün", range: () => [addDays(new Date(), -29), new Date()] },
  { label: "Gelecek 30", range: () => [new Date(), addDays(new Date(), 29)] },
  { label: "Gelecek 60", range: () => [new Date(), addDays(new Date(), 59)] },
  { label: "Gelecek 90", range: () => [new Date(), addDays(new Date(), 89)] },
];
const DAY_GROUPS = [["dinner-cruise", "Dinner"], ["sunset-cruise", "Sunset"], ["small-group-sunset", "Small"], ["daytime", "Daytime"]];

function YemekMark({ value, onChange, disabled, testId }) {
  const next = value === null || value === undefined ? true : value === true ? false : null;
  const cls = value === true ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
    : value === false ? "bg-red-500/20 text-red-300 border-red-500/50"
    : "bg-transparent text-slate-600 border-slate-700 hover:border-slate-500";
  return (
    <button type="button" disabled={disabled} data-testid={testId}
      onClick={(e) => { e.stopPropagation(); onChange(next); }}
      title={value === true ? "Yemekler yendi ✔ (tıkla: ✘)" : value === false ? "Yemek sorunu ✘ (tıkla: temizle)" : "Yemekler yendi mi? (tıkla: ✔)"}
      className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${cls} disabled:opacity-30`}>
      {value === true ? <Check className="w-3 h-3" strokeWidth={3} /> : value === false ? <X className="w-3 h-3" strokeWidth={3} /> : <span className="w-1 h-1 rounded-full bg-current" />}
    </button>
  );
}

function BadBadge({ reviews, testId, big, title }) {
  if (!reviews?.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} onClick={(e) => e.stopPropagation()}
          className={big
            ? "font-mono font-bold text-red-300 hover:text-red-200 underline decoration-dotted underline-offset-2"
            : "inline-flex items-center gap-0.5 text-[10px] font-bold text-red-200 bg-red-600/40 border border-red-500/50 rounded px-1 leading-4 hover:bg-red-600/60"}>
          {big ? reviews.length : <><Frown className="w-3 h-3" />{reviews.length}</>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] max-h-[360px] overflow-auto bg-[#0F1629] border-red-500/30 text-slate-200 p-3 space-y-2">
        <div className="text-xs font-bold text-red-300 uppercase tracking-wide">{title || "Beğenmeyen yorumlar"} ({reviews.length})</div>
        {reviews.map((r) => (
          <div key={r.id} className="border border-white/10 rounded-md p-2 text-xs" data-testid={`bad-review-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-100 truncate">{r.reviewer || "Anonim"}</span>
              <span className="flex items-center gap-1 text-amber-300 shrink-0">{r.rating}<Star className="w-3 h-3 fill-current" /><span className="text-slate-500 ml-1 uppercase">{r.source}</span>{r.boat && <span className="text-sky-300/80 ml-1">· {r.boat}</span>}</span>
            </div>
            {r.title && <div className="text-slate-300 font-medium mt-0.5">{r.title}</div>}
            <div className="text-slate-400 mt-0.5 line-clamp-3">{r.snippet}</div>
            {r.garson && <div className="text-[10px] text-sky-300 mt-1">Garson: {r.garson}</div>}
          </div>
        ))}
        <Link to="/reviews" className="block text-center text-[11px] text-blue-300 hover:text-blue-200 pt-1">Yorumlar sayfasında aç →</Link>
      </PopoverContent>
    </Popover>
  );
}

function Cell({ row, col, cell, showGelen, onMark }) {
  const link = `/live-sheet?date=${row.date}${GROUP_CAT[col.group] ? `&category=${GROUP_CAT[col.group]}` : ""}`;
  if (!cell) return <td className="border border-white/[0.05] text-center text-slate-700 text-xs">·</td>;
  const showG = showGelen && (row.is_today || row.is_past) && cell.checkin_rows > 0;
  const diff = cell.gelen - cell.rez;
  return (
    <td className="border border-white/[0.05] p-0" data-testid={`cell-${row.date}-${col.key}`}>
      <div className="flex items-center gap-0.5 px-1 py-1">
        <div className="w-3.5 shrink-0 flex items-center justify-center">
          {(row.is_past || row.is_today) && (
            <YemekMark value={cell.yemek_ok} onChange={(v) => onMark(row.date, col.key, v)} testId={`yemek-${row.date}-${col.key}`} />
          )}
        </div>
        <Link to={link} className="flex-1 text-center rounded hover:bg-white/[0.05] px-1 py-0.5 whitespace-nowrap" title={`${col.label} · ${cell.bookings} rezervasyon · ${fmtN(cell.rez)} pax${cell.checkin_rows ? ` · gelen ${fmtN(cell.gelen)}` : ""}`}>
          <span className={`font-mono font-bold text-[14px] ${row.is_past ? "text-slate-200" : "text-blue-200"}`}>{fmtN(cell.rez)}</span>
          {showG && (
            <span className={`font-mono text-[11px] ml-1 ${diff < 0 ? "text-orange-300" : diff > 0 ? "text-emerald-300" : "text-slate-500"}`}>/{fmtN(cell.gelen)}</span>
          )}
        </Link>
        <div className="w-5 shrink-0 flex items-center justify-end">
          <BadBadge reviews={cell.bad_reviews} testId={`bad-${row.date}-${col.key}`} />
        </div>
      </div>
    </td>
  );
}

export default function DailySummary() {
  const [start, setStart] = useState(iso(startOfMonth(new Date())));
  const [end, setEnd] = useState(iso(endOfMonth(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGelen, setShowGelen] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/daily-summary`, { params: { start, end }, headers });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Özet yüklenemedi");
    }
    setLoading(false);
  }, [start, end, headers]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const onMark = async (date, colKey, value) => {
    setData((d) => {
      if (!d) return d;
      const rows = d.rows.map((r) => {
        if (r.date !== date) return r;
        const cells = { ...r.cells, [colKey]: { ...r.cells[colKey], yemek_ok: value } };
        const marks = Object.values(cells).filter((c) => c.rez > 0).map((c) => c.yemek_ok);
        const yemek_ok = marks.some((v) => v === false) ? false : marks.length && marks.every((v) => v === true) ? true : null;
        return { ...r, cells, day: { ...r.day, yemek_ok } };
      });
      return { ...d, rows };
    });
    try {
      await axios.put(`${API}/api/reports/daily-summary/mark`, { date, col_key: colKey, yemek_ok: value }, { headers });
    } catch (e) {
      toast.error("İşaret kaydedilemedi");
      fetchData();
    }
  };

  const shift = (days) => {
    setStart(iso(addDays(new Date(start), days)));
    setEnd(iso(addDays(new Date(end), days)));
  };

  const copyTsv = () => {
    if (!data) return;
    const head = ["Tarih", ...data.columns.map((c) => c.label), "Toplam", "Gelen", "Kötü Yorum"];
    const lines = data.rows.map((r) => [
      format(new Date(r.date), "dd.MM.yyyy"),
      ...data.columns.map((c) => r.cells[c.key]?.rez ?? ""),
      r.day.rez, r.day.gelen, r.day.bad,
    ].join("\t"));
    lines.push(["TOPLAM", ...data.columns.map((c) => data.totals[c.key]?.rez ?? ""), data.grand.rez, data.grand.gelen, data.grand.bad].join("\t"));
    navigator.clipboard.writeText([head.join("\t"), ...lines].join("\n"));
    toast.success("Tablo kopyalandı — Excel'e yapıştırabilirsiniz");
  };

  const dayCount = data?.rows?.length || 0;
  return (
    <div data-testid="daily-summary-page">
      <div className="page-header flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Günlük Özet</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tarih × tekne pax tablosu · rezerve <span className="text-slate-400">/ gelen</span> · yemek ✔ · beğenmeyen</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => shift(-dayCount)} className="p-1.5 rounded bg-black/30 border border-white/10 text-slate-300 hover:text-white" data-testid="range-prev" title="Önceki dönem"><ChevronLeft className="w-4 h-4" /></button>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="range-start"
            className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-slate-200" />
          <span className="text-slate-500">—</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="range-end"
            className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-slate-200" />
          <button onClick={() => shift(dayCount)} className="p-1.5 rounded bg-black/30 border border-white/10 text-slate-300 hover:text-white" data-testid="range-next" title="Sonraki dönem"><ChevronRight className="w-4 h-4" /></button>
          <div className="hidden md:flex items-center gap-1 ml-2">
            {PRESETS.map((p) => (
              <button key={p.label} onClick={() => { const [a, b] = p.range(); setStart(iso(a)); setEnd(iso(b)); }}
                className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-blue-600/20 hover:text-blue-200" data-testid={`preset-${p.label}`}>{p.label}</button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-300 ml-2 cursor-pointer select-none">
            <input type="checkbox" checked={showGelen} onChange={(e) => setShowGelen(e.target.checked)} data-testid="toggle-gelen" className="accent-blue-500" /> Gelen
          </label>
          <button onClick={copyTsv} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10" data-testid="copy-tsv"><Copy className="w-3.5 h-3.5" /> Excel'e kopyala</button>
          <button onClick={fetchData} className="p-1.5 rounded bg-black/30 border border-white/10 text-slate-300 hover:text-white" data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      <div className="page-body">
        {data && (
          <div className="flex flex-wrap gap-3 mb-4" data-testid="summary-kpis">
            {[["Rezerve Pax", fmtN(data.grand.rez), "text-blue-200", null], ["Gelen Pax", fmtN(data.grand.gelen), "text-emerald-300", null],
              ["Rezervasyon", fmtN(data.grand.bookings), "text-slate-200", null],
              ["Beğenmeyen Yorum", fmtN(data.grand.bad), data.grand.bad ? "text-red-300" : "text-slate-400",
                data.grand.bad_pct != null ? `%${data.grand.bad_pct.toLocaleString("tr-TR")} · yorum yapan ${fmtN(data.grand.reviews)} rezervenin ${fmtN(data.grand.bad)}'i` : null]].map(([l, v, c, sub]) => (
              <div key={l} className="glass-card px-4 py-2.5 min-w-[150px]" data-testid={`kpi-${l}`}>
                <div className="flex items-baseline gap-2">
                  <div className={`text-2xl font-extrabold font-mono leading-none ${c}`}>{v}</div>
                  {sub && <div className="text-xs font-semibold text-red-200/80" data-testid="kpi-bad-pct">{sub.split(" · ")[0]}</div>}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{l}{sub ? <span className="normal-case tracking-normal text-slate-600"> — {sub.split(" · ")[1]}</span> : null}</div>
              </div>
            ))}
            {(() => {
              const g = data.grand;
              const pctRev = g.bookings ? (g.reviews * 100) / g.bookings : null;
              const pctBad = g.reviews ? (g.bad * 100) / g.reviews : null;
              const fmtP = (p) => (p == null ? "—" : `%${p.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`);
              const steps = [
                ["Rezervasyon", fmtN(g.bookings), "rez", "text-slate-100"],
                ["Müşteri gezdi", fmtN(g.gelen), "pax", "text-emerald-300"],
                ["Yorum yaptı", fmtP(pctRev), `${fmtN(g.reviews)} yorum`, "text-sky-300"],
                ["Beğenmedi", fmtP(pctBad), `${fmtN(g.bad)} yorum`, g.bad ? "text-red-300" : "text-slate-400"],
              ];
              return (
                <div className="glass-card px-4 py-2.5 flex items-center gap-3 flex-1 min-w-[520px]" data-testid="funnel-card">
                  {steps.map(([l, v, sub, c], i) => (
                    <div key={l} className="flex items-center gap-3">
                      {i > 0 && <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />}
                      <div data-testid={`funnel-${i}`}>
                        <div className={`text-xl font-extrabold font-mono leading-none ${c}`}>{v}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{l} <span className="normal-case tracking-normal text-slate-600">· {sub}</span></div>
                      </div>
                    </div>
                  ))}
                  <div className="ml-auto text-[11px] text-slate-400 leading-snug max-w-[260px]" data-testid="funnel-sentence">
                    {fmtN(g.bookings)} rezervasyonla <b className="text-emerald-300">{fmtN(g.gelen)}</b> müşteri gezdi; rezervasyonların <b className="text-sky-300">{fmtP(pctRev)}</b>'i yorum yaptı, yorum yapanların <b className="text-red-300">{fmtP(pctBad)}</b>'i beğenmedi.
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="glass-card" style={{ height: "calc(100vh - 215px)", minHeight: 420, overflow: "auto" }} data-testid="summary-scroll">
          {!data && loading && <div className="p-10 text-center text-slate-500 text-sm">Yükleniyor…</div>}
          {data && (() => {
            const dayGroups = DAY_GROUPS.filter(([g]) => data.groups.some((x) => x.slug === g));
            const dayCols = dayGroups.length + 3;
            return (
            <table className="border-separate border-spacing-0 text-sm min-w-full" data-testid="summary-table">
              <thead className="sticky top-0 z-20 shadow-[0_1px_0_rgba(255,255,255,0.08)]">
                <tr className="bg-[#0F1629]">
                  <th rowSpan={2} className="sticky left-0 z-30 bg-[#0F1629] border border-white/[0.06] px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-400 min-w-[104px]">Tarih</th>
                  {data.groups.map((g) => (
                    <th key={g.slug} colSpan={g.span} className={`border border-white/[0.06] border-b-2 px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] ${GROUP_TONE[g.slug] || GROUP_TONE.other}`}>{g.label}</th>
                  ))}
                  <th colSpan={dayCols} className="border border-white/[0.06] border-b-2 border-white/20 px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-200">GÜN</th>
                </tr>
                <tr className="bg-[#0F1629]">
                  {data.columns.map((c) => (
                    <th key={c.key} className={`border border-white/[0.06] px-1.5 py-1.5 text-[11px] font-semibold whitespace-nowrap ${c.unassigned ? "text-slate-500 italic" : "text-slate-200"}`} data-testid={`col-${c.key}`}>{c.label}</th>
                  ))}
                  {dayGroups.map(([g, label]) => (
                    <th key={g} className={`border border-white/[0.06] px-2 py-1.5 text-xs font-semibold ${(GROUP_TONE[g] || "").split(" ")[0]}`} data-testid={`col-day-${g}`}>{label}</th>
                  ))}
                  <th className="border border-white/[0.06] px-2 py-1.5 text-xs font-semibold text-white">Toplam</th>
                  <th className="border border-white/[0.06] px-2 py-1.5 text-xs font-semibold text-slate-200" title="Yemekler yenmiş mi (tüm tekneler ✔ ise ✔)">Yemek</th>
                  <th className="border border-white/[0.06] px-2 py-1.5 text-xs font-semibold text-slate-200 whitespace-nowrap" title="1–3 yıldızlı yorum sayısı (tekne bazlı + rezervasyonla eşleşmemiş)">Kötü Yorum</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const wk = r.weekday >= 5;
                  return (
                    <tr key={r.date} className={`${r.is_today ? "bg-blue-500/10" : wk ? "bg-white/[0.025]" : ""} ${!r.is_past && !r.is_today ? "opacity-80" : ""}`} data-testid={`row-${r.date}`}>
                      <td className={`sticky left-0 z-10 border border-white/[0.06] px-2 py-1.5 whitespace-nowrap ${r.is_today ? "bg-[#131c33]" : "bg-[#0F1629]"}`}>
                        <span className="font-mono font-semibold text-slate-100">{format(new Date(r.date), "dd.MM")}</span>
                        <span className={`ml-2 text-xs ${wk ? "text-amber-300" : "text-slate-500"}`}>{format(new Date(r.date), "EEE", { locale: tr })}</span>
                        {r.is_today && <span className="ml-2 text-[9px] font-bold uppercase text-blue-300">bugün</span>}
                      </td>
                      {data.columns.map((c) => <Cell key={c.key} row={r} col={c} cell={r.cells[c.key]} showGelen={showGelen} onMark={onMark} />)}
                      {dayGroups.map(([g]) => (
                        <td key={g} className="border border-white/[0.06] px-2 py-1.5 text-center bg-white/[0.02]" data-testid={`day-${g}-${r.date}`}>
                          <span className={`font-mono font-bold text-[14px] ${r.group_totals?.[g] ? (GROUP_TONE[g] || "").split(" ")[0] : "text-slate-700"}`}>{r.group_totals?.[g] ? fmtN(r.group_totals[g]) : "·"}</span>
                        </td>
                      ))}
                      <td className="border border-white/[0.06] px-2 py-1.5 text-center bg-white/[0.04]" data-testid={`day-total-${r.date}`}>
                        <span className="font-mono font-extrabold text-[15px] text-white">{fmtN(r.day.rez)}</span>
                        {showGelen && (r.is_past || r.is_today) && r.day.gelen > 0 && <span className="font-mono text-[11px] ml-1 text-slate-400">/{fmtN(r.day.gelen)}</span>}
                      </td>
                      <td className="border border-white/[0.06] px-2 py-1.5 text-center" data-testid={`day-yemek-${r.date}`}>
                        {r.day.yemek_ok === true && <Check className="w-4 h-4 text-emerald-400 inline" strokeWidth={3} />}
                        {r.day.yemek_ok === false && <X className="w-4 h-4 text-red-400 inline" strokeWidth={3} />}
                        {r.day.yemek_ok == null && <span className="text-slate-700">—</span>}
                      </td>
                      <td className="border border-white/[0.06] px-2 py-1.5 text-center" data-testid={`day-bad-${r.date}`}>
                        {r.day.bad > 0 ? <BadBadge big reviews={r.day.bad_reviews} testId={`day-bad-btn-${r.date}`} title={`${format(new Date(r.date), "dd.MM")} beğenmeyen yorumlar`} /> : <span className="text-slate-700">—</span>}
                        {r.day.unmatched_bad > 0 && <span className="block text-[9px] text-slate-500 leading-none mt-0.5" title="Rezervasyonla eşleşmeyen (tur tarihine göre)">{r.day.unmatched_bad} eşleşmemiş</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-20">
                <tr className="bg-[#111a30]" data-testid="totals-row">
                  <td className="sticky left-0 z-30 bg-[#111a30] border border-white/[0.08] px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-300">Toplam</td>
                  {data.columns.map((c) => (
                    <td key={c.key} className="border border-white/[0.08] px-2 py-2 text-center">
                      <span className="font-mono font-bold text-slate-100">{fmtN(data.totals[c.key]?.rez)}</span>
                      {showGelen && data.totals[c.key]?.gelen > 0 && <span className="font-mono text-[11px] ml-1 text-slate-500">/{fmtN(data.totals[c.key].gelen)}</span>}
                      {data.totals[c.key]?.bad > 0 && (
                        <span className="block text-[10px] text-red-300 font-bold" data-testid={`total-bad-${c.key}`}>
                          {data.totals[c.key].bad}/{data.totals[c.key].reviews} kötü{data.totals[c.key].bad_pct != null ? ` · %${data.totals[c.key].bad_pct.toLocaleString("tr-TR")}` : ""}
                        </span>
                      )}
                    </td>
                  ))}
                  {dayGroups.map(([g]) => (
                    <td key={g} className={`border border-white/[0.08] px-2 py-2 text-center font-mono font-bold ${(GROUP_TONE[g] || "").split(" ")[0]}`} data-testid={`total-day-${g}`}>{fmtN(data.grand.group_totals?.[g])}</td>
                  ))}
                  <td className="border border-white/[0.08] px-2 py-2 text-center font-mono font-extrabold text-white">{fmtN(data.grand.rez)}{showGelen && data.grand.gelen > 0 && <span className="text-[11px] font-normal text-slate-400">/{fmtN(data.grand.gelen)}</span>}</td>
                  <td className="border border-white/[0.08]" />
                  <td className="border border-white/[0.08] px-2 py-2 text-center font-mono font-bold text-red-300 whitespace-nowrap">
                    {data.grand.bad || "—"}
                    {data.grand.bad_pct != null && <span className="block text-[10px] font-semibold text-red-200/80" title={`${fmtN(data.grand.reviews)} yorum`}>%{data.grand.bad_pct.toLocaleString("tr-TR")} · {fmtN(data.grand.reviews)} yorum</span>}
                  </td>
                </tr>
              </tfoot>
            </table>
            );
          })()}
        </div>
        <p className="text-[11px] text-slate-600 mt-2">Hücreye tıklayınca o günün Canlı Defteri açılır · sol kutucuk: yemekler yendi ✔ / sorun ✘ (elle işaretlenir) · kırmızı rozet: 1–3★ yorumlar (tıklayınca listelenir) · Kötü yorum %’si = kötü yorum / yorum yapan rezervasyon sayısı (tüm yıldızlar)</p>
      </div>
    </div>
  );
}
